/**
 * Dashboard Page - User Dashboard
 *
 * Main dashboard showing user info, scan history, usage, etc.
 */

import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, useNavigate } from 'react-router-dom';
import { ScanLine, BarChart3, Zap } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { PageBackground } from '@/components/ui/PageLayout';
import { PageSubHeader } from '@/components/ui/PageSubHeader';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { PageLoader } from '@/components/ui/PageLoader';
import { useAuth } from '@/contexts/AuthContext';
import { useUserInfo } from '@/hooks/useUserInfo';
import { REPORTS_QUERY_KEYS, useReportStats } from '@/hooks/useReports';
import { RecentScansWidget, ScanInsightsWidget, ScanModal } from '@/components/dashboard';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const GET_STARTED_STEPS: { label: string; desc: string; scanCta?: true; accountCta?: true }[] = [
  {
    label: 'Get your API key',
    desc: 'Create and copy your API key from the API Keys tab in your account settings',
    accountCta: true,
  },
  {
    label: 'Scan a repository',
    desc: 'Use the GitHub Scanner to scan repositories directly from your browser',
    scanCta: true,
  },
  {
    label: 'Use Claude to review code',
    desc: 'Ask Claude to scan repositories and analyze security issues using our MCP tools',
  },
];

export default function DashboardPage() {
  useDocumentTitle('Dashboard');
  const { authenticated, loading: authLoading } = useAuth();
  const { data: userInfo, isLoading } = useUserInfo();
  const { data: statsData } = useReportStats();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Auto-open scan modal when returning from GitHub OAuth callback
  const [scanModalOpen, setScanModalOpen] = useState(() => {
    const p = new URLSearchParams(window.location.search);
    return p.get('github_linked') === '1' || p.get('github_error') === '1';
  });

  // Invalidate dashboard queries when the modal closes so widgets refresh
  const handleScanModalChange = useCallback(
    (open: boolean) => {
      setScanModalOpen(open);
      if (!open) {
        queryClient.invalidateQueries({ queryKey: ['userInfo', 'me'] });
        queryClient.invalidateQueries({ queryKey: REPORTS_QUERY_KEYS.all });
      }
    },
    [queryClient]
  );

  // Redirect to login if not authenticated
  if (!authenticated && !authLoading) {
    return <Navigate to="/login" replace />;
  }

  if (authLoading || isLoading) return <PageLoader message="Loading dashboard..." />;

  const scansRemaining = userInfo?.limits?.scansRemaining ?? 0;
  const canRunScan = scansRemaining === -1 || scansRemaining > 0;

  return (
    <PageBackground>
      <AppHeader />
      <PageSubHeader
        title={`Welcome back, ${userInfo?.user?.name?.split(' ')[0] || userInfo?.user?.email?.split('@')[0] || 'User'}!`}
        subtitle="Start scanning GitHub repositories with AI"
        action={
          canRunScan ? (
            <Button
              onClick={() => setScanModalOpen(true)}
              className="bg-green-600 hover:bg-green-700 text-white gap-2"
            >
              <ScanLine className="w-4 h-4" />
              Run a Scan
            </Button>
          ) : (
            <Button
              onClick={() => (window.location.href = '/pricing')}
              className="bg-green-600 hover:bg-green-700 text-white gap-2"
            >
              <ScanLine className="w-4 h-4" />
              Upgrade to Scan
            </Button>
          )
        }
      />

      <ScanModal open={scanModalOpen} onOpenChange={handleScanModalChange} />

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 animate-fade-in">
        {/* Compact quota row + insights strip */}
        <div className="mb-8 space-y-3">
          {/* Quota chips */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/60 rounded-lg px-4 py-2">
              <BarChart3 className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-xs text-zinc-400">Remaining</span>
              <span className="text-sm font-bold text-white">
                {scansRemaining === -1 ? 'Unlimited' : scansRemaining}
              </span>
              {(userInfo?.limits?.scansPerMonth ?? 0) > 0 && (
                <span className="text-[10px] text-zinc-600">/ mo</span>
              )}
            </div>
            <div className="flex items-center gap-2 bg-zinc-800/60 border border-zinc-700/60 rounded-lg px-4 py-2">
              <Zap className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-xs text-zinc-400">Scans run</span>
              <span className="text-sm font-bold text-white">
                {statsData?.totalScans ?? userInfo?.limits?.scansUsed ?? 0}
              </span>
            </div>
            {!(statsData?.totalScans ?? userInfo?.limits?.scansUsed) && canRunScan && (
              <button
                onClick={() => setScanModalOpen(true)}
                className="text-xs text-green-400 hover:text-green-300 underline transition-colors"
              >
                Run your first scan →
              </button>
            )}
          </div>

          {/* Insights strip — only visible once scans exist */}
          <ScanInsightsWidget />
        </div>

        {/* Recent Scans */}
        <div className="mb-8">
          <RecentScansWidget limit={5} onRunScan={() => setScanModalOpen(true)} />
        </div>

        {/* Get Started */}
        <div>
          <Card className="bg-zinc-800 border-zinc-700 transition-all duration-300 hover:border-zinc-600">
            <CardHeader>
              <CardTitle className="text-white">Get Started</CardTitle>
              <CardDescription className="text-zinc-400">
                Start scanning GitHub repositories with AI
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ol className="space-y-6">
                {GET_STARTED_STEPS.map(({ label, desc, scanCta, accountCta }, i) => (
                  <li key={label} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-sm font-bold flex-shrink-0 mt-0.5">
                      {i + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-white font-medium text-base">{label}</h3>
                      <p className="text-sm text-zinc-400 mt-0.5">{desc}</p>
                      {scanCta && (
                        <button
                          onClick={() =>
                            canRunScan
                              ? setScanModalOpen(true)
                              : (window.location.href = '/pricing')
                          }
                          className="mt-2 text-xs text-green-400 hover:text-green-300 underline transition-colors"
                        >
                          {canRunScan ? 'Open scanner →' : 'Upgrade to scan →'}
                        </button>
                      )}
                      {accountCta && (
                        <button
                          onClick={() => navigate('/account?tab=api')}
                          className="mt-2 text-xs text-green-400 hover:text-green-300 underline transition-colors"
                        >
                          Go to API Keys →
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>
      </main>
    </PageBackground>
  );
}
